import React, { FunctionComponent, ReactNode, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { MenuItem } from "@mui/material";
import useSwitch from "@react-hook/switch";
import { useFlags } from "../../services/utilities/launch_darkly_helper";

import "../../../css/nav_bar/nav_bar_panels_menu.css";
import BatchIfcFloorSelectionDialog from "../dialogs/batch_ifc_floor_selection_dialog";
import downloadDeviatedTsv from "../../actions/viewer_page/download_deviated_tsv";
import exportBatchIfc from "../../actions/viewer_page/export_batch_ifc";
import ExportDeviationsIfcDialog, { DeviationsIfcType } from "../dialogs/export_deviations_ifc_dialog";
import ExportDeviationsIfcWizard from "../dialogs/export_deviations_ifc_wizard";
import getExportStarted from "../../services/getters/get_export_started";
import getProjectName from "../../services/getters/project_getters/get_project_name";
import getProcoreAccessToken from "../../actions/viewer_page/procore/get_procore_access_token";
import getProjectId from "../../services/getters/project_getters/get_project_id";
import NavBarDropdown from "./nav_bar_dropdown";
import ProcoreRequestType from "../../services/procore_api_services/procore_request_type";
import usePopover from "../../services/component_helpers/component_effects/use_popover";

import type { ReduxStore } from "../../services/reducers/root_reducer";
import downloadProjectDataTsv from "../../actions/viewer_page/download_project_data_tsv";

// The second-level nav's design says "Export" and sizes the button to fit it, and draws the
// chevron its own way; the defaults keep the legacy nav unchanged. Leaving endIcon unset falls
// through to NavBarDropdown's own.
export type OwnProps = { label?: string, endIcon?: ReactNode };

export type Props = OwnProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

export const NavBarExportDropdownMenu: FunctionComponent<Props> = (props) => {
  const {
    label = "Exports",
    endIcon,
    exportStarted,
    exportBatchIfc,
    downloadDeviatedTsv,
    downloadProjectDataTsv,
    getProcoreAccessToken,
    projectId,
    projectName
  } = props;

  const [openState, anchorEl, handleOpen, onClose] = usePopover();
  const [exportDeviationsDialogOpen, toggleExportDeviationsDialog] = useSwitch(false);
  const [exportDeviationsWizardOpen, toggleExportDeviationsWizard] = useSwitch(false);
  const [exportAsBuiltWizardOpen, toggleExportAsBuiltWizard] = useSwitch(false);
  const [batchExportIfcDialogOpen, toggleBatchExportIfcDialogOpen] = useSwitch(false);
  const {
    pushDeviationsToProcore,
    downloadProjectDataTsv: downloadProjectDataTsvFlag,
    ifcDownloadImprovements: ifcDownloadImprovementsFlag,
    ifcExportTradeFilter: ifcExportTradeFilterFlag,
    ifcExportAsBuiltThreshold: ifcExportAsBuiltThresholdFlag,
    ifcExportAsBuiltTrades: ifcExportAsBuiltTradesFlag,
  } = useFlags();

  const [batchDownloadSelectedIfcType, setBatchDownloadSelectedIfcType] = useState<string | null>(null);
  const batchDownloadIfc = (ifcType: string) => {
    if (ifcType) {
      setBatchDownloadSelectedIfcType(ifcType);
      toggleBatchExportIfcDialogOpen.on();
    } else {
      setBatchDownloadSelectedIfcType(null);
    }
  }

  return <div>
    <NavBarDropdown
      className="NavBarPanelsMenu-button btn-export"
      label={label}
      popoverProps={{
        open: openState,
        anchorEl,
        handleOpen,
        onClose
      }}
      slotProps={{
        ...endIcon && { button: { endIcon } },
        menu: {
          className: "NavBarPanelsMenu"
        }
      }}
    >

      <MenuItem className="NavBarKebabMenu-asBuilt NavBarKebabMenu-menuItem" disabled={exportStarted} onClick={() => {
        if (ifcExportAsBuiltThresholdFlag) {
          // Close the MUI dropdown before the Nova <Modal>, or the two focus
          // traps duel and blow the call stack on Next.
          onClose();
          toggleExportAsBuiltWizard.on();
        } else {
          batchDownloadIfc("as_built");
        }
      }}>
        As-built (.ifc)
      </MenuItem>
      <MenuItem className="NavBarKebabMenu-deviations NavBarKebabMenu-menuItem" disabled={exportStarted} onClick={() => {
        // Close this MUI dropdown before opening the Nova <Modal> wizard, or the two focus traps duel and blow the call stack on Next.
        onClose();
        (ifcDownloadImprovementsFlag ? toggleExportDeviationsWizard.on : toggleExportDeviationsDialog.on)();
      }}>
        Deviations (.ifc)
      </MenuItem>
      <MenuItem className="NavBarKebabMenu-notBuilt NavBarKebabMenu-menuItem" disabled={exportStarted} onClick={() => batchDownloadIfc("not_built")}>
        Not Built (.ifc)
      </MenuItem>
      <MenuItem className="NavBarKebabMenu-elementsTsv NavBarKebabMenu-menuItem" disabled={exportStarted} onClick={downloadDeviatedTsv}>
        Elements (.tsv)
      </MenuItem>
      {downloadProjectDataTsvFlag ?
        <MenuItem className="NavBarKebabMenu-projectDataTsv NavBarKebabMenu-menuItem" disabled={exportStarted} onClick={downloadProjectDataTsv}>
          Project Data (.tsv)
        </MenuItem>
        : null
      }
      {pushDeviationsToProcore ?
       <MenuItem className="NavBarKebabMenu-pushDeviationsToProcore NavBarPanelsMenu-menuItem" onClick={() => getProcoreAccessToken(ProcoreRequestType.DEVIATIONS)}>
         Push Raw Data to Procore
       </MenuItem> :
       null}
    </NavBarDropdown>

    <ExportDeviationsIfcDialog
      open={exportDeviationsDialogOpen}
      onClose={toggleExportDeviationsDialog.off}
      onClickOk={(deviationsIfcType: DeviationsIfcType) => {
        toggleExportDeviationsDialog.off();
        batchDownloadIfc(deviationsIfcType);
      }}
    />

    {ifcDownloadImprovementsFlag ?
      <ExportDeviationsIfcWizard
        open={exportDeviationsWizardOpen}
        projectId={projectId}
        projectName={projectName}
        tradeFilterEnabled={!!ifcExportTradeFilterFlag}
        onClose={toggleExportDeviationsWizard.off}
        onExport={(ifcType, floorIds, minimumDeviationMetres, tradeCodes, fileNaming) =>
          exportBatchIfc(floorIds, ifcType, minimumDeviationMetres, tradeCodes, undefined, fileNaming)}
      />
      : null
    }

    {ifcExportAsBuiltThresholdFlag ?
      <ExportDeviationsIfcWizard
        variant="asBuilt"
        open={exportAsBuiltWizardOpen}
        projectId={projectId}
        projectName={projectName}
        // Rides its own flag on top of the threshold flag, so trades without
        // threshold is unreachable by construction.
        tradeFilterEnabled={!!ifcExportAsBuiltTradesFlag}
        onClose={toggleExportAsBuiltWizard.off}
        onExport={(ifcType, floorIds, minimumDeviationMetres, tradeCodes, fileNaming) =>
          exportBatchIfc(floorIds, ifcType, minimumDeviationMetres, tradeCodes, true, fileNaming)}
      />
      : null
    }

    <BatchIfcFloorSelectionDialog open={batchExportIfcDialogOpen}
                                  onClose={toggleBatchExportIfcDialogOpen.off}
                                  onClickOk={floorIds => {
                                    exportBatchIfc(floorIds, batchDownloadSelectedIfcType);
                                    toggleBatchExportIfcDialogOpen.off();
                                  }}
                                  title={batchDownloadSelectedIfcType} projectId={projectId}></BatchIfcFloorSelectionDialog>
  </div>


};

const mapStateToProps = (state: ReduxStore, props: OwnProps) => {
  return {
    exportStarted: getExportStarted(state, props),
    projectId: getProjectId(state, props),
    projectName: getProjectName(state, props),
  };
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
  exportBatchIfc,
  downloadDeviatedTsv,
  downloadProjectDataTsv,
  getProcoreAccessToken,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(NavBarExportDropdownMenu);
